import { keepPreviousData, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import type {
  ClientStatsPreset,
  ClientStatsSeriesResponse,
  ClientStatsSortBy,
  ClientStatsSortOrder,
  ClientStatsSummary,
} from '../../services/consultant/client-stats.service.interface';
import { MySitesService } from '../../services/my-sites/my-sites.service';
import type {
  IMySitesService,
  MySitesStatsResponse,
} from '../../services/my-sites/my-sites.service.interface';

export const MY_SITES_PAGE_SIZE = 50;

export interface MySitesFilters {
  search: string;
  sortBy?: ClientStatsSortBy;
  sortOrder?: ClientStatsSortOrder;
}

const EMPTY_SUMMARY: ClientStatsSummary = { critical: 0, warning: 0, ok: 0, na: 0 };

/** Namespace di query key proprio: MAI condiviso con `consultant/client-stats` (perimetri diversi). */
export const mySitesQueryKeys = {
  all: ['my-sites'] as const,
  stats: (preset: ClientStatsPreset, page: number, pageSize: number, filters?: MySitesFilters) =>
    [
      'my-sites',
      'stats',
      preset,
      page,
      pageSize,
      filters?.search.trim() || null,
      filters?.sortBy ?? null,
      filters?.sortOrder ?? null,
    ] as const,
  access: (tenantId: string | null) => ['my-sites', 'access', tenantId ?? 'no-tenant'] as const,
  series: (tenantId: string, preset: ClientStatsPreset) =>
    ['my-sites', 'series', tenantId, preset] as const,
};

/** Query portfolio "Le mie sedi": paginata + ricerca/ordinamento server-side. */
export function useMySitesStats(
  preset: ClientStatsPreset,
  page: number,
  filters: MySitesFilters = { search: '' },
  pageSize = MY_SITES_PAGE_SIZE,
) {
  const mySitesService = useInjection<IMySitesService>(MySitesService);

  const query: UseQueryResult<MySitesStatsResponse> = useQuery({
    queryKey: mySitesQueryKeys.stats(preset, page, pageSize, filters),
    queryFn: () => {
      const term = filters.search.trim();
      return mySitesService.getStats({
        preset,
        page,
        pageSize,
        search: term || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
    },
    placeholderData: keepPreviousData,
  });

  return {
    portfolio: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    pageSize: query.data?.pageSize ?? pageSize,
    summary: query.data?.summary ?? EMPTY_SUMMARY,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** Serie storica di UNA sede del perimetro; disabled senza tenantId. */
export function useMySiteSeries(tenantId: string | null, preset: ClientStatsPreset) {
  const mySitesService = useInjection<IMySitesService>(MySitesService);

  const query: UseQueryResult<ClientStatsSeriesResponse> = useQuery({
    queryKey: tenantId ? mySitesQueryKeys.series(tenantId, preset) : ['my-sites', 'series', 'none'],
    queryFn: () => mySitesService.getSeries(tenantId as string, preset),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });

  return {
    series: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
